// SPDX-License-Identifier: BUSL-1.1

/**
 * Individual moderation item card.
 *
 * Displays tool name, policy gate badge, session link, collapsible JSON
 * input preview, timestamp, status, and approve/reject action buttons.
 *
 */

import { useState } from 'react';
import { CheckCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ModerationRejectDialog } from '@/components/moderation-reject-dialog';
import { relativeTime } from '@/lib/format';
import type { ModerationItem } from '@/types/api';
import { GATE_COLORS, MODERATION_STATUS_COLORS } from '@/types/api';

interface ModerationItemCardProps {
  item: ModerationItem;
  onApprove: (id: string) => void;
  onReject: (id: string, denyMessage: string) => void;
  isResolving: boolean;
}

export function ModerationItemCard({
  item,
  onApprove,
  onReject,
  isResolving,
}: ModerationItemCardProps) {
  const [expanded, setExpanded] = useState(false);
  const isPending = item.status === 'pending';

  return (
    <Card size="sm">
      <CardContent className="space-y-3">
        {/* Top row: tool name + gate badge + timestamp + status */}
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-semibold text-neutral-100">{item.toolName}</span>
          <span
            className={`inline-flex rounded px-2 py-0.5 text-xs ${GATE_COLORS[item.policyGate]}`}
          >
            {item.policyGate}
          </span>
          <Badge variant="outline" className={MODERATION_STATUS_COLORS[item.status]}>
            {item.status}
          </Badge>
          <span className="ml-auto text-xs text-neutral-500">{relativeTime(item.createdAt)}</span>
        </div>

        {/* Session link + approval metadata */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-400">
          <span>Session:</span>
          <Link
            to={`/agents/${item.sessionId}`}
            className="font-mono text-blue-400 hover:underline"
          >
            {item.sessionId.slice(0, 12)}...
          </Link>
          {item.riskClassification && (
            <Badge variant="outline" className="bg-orange-950/40 text-orange-300">
              {item.riskClassification} risk
            </Badge>
          )}
          {item.policyVersion && (
            <span className="rounded bg-neutral-800 px-2 py-0.5 text-[11px] text-neutral-300">
              {item.policyVersion}
            </span>
          )}
          {item.expiresAt && (
            <span className="text-[11px] text-neutral-500">
              expires {relativeTime(item.expiresAt)}
            </span>
          )}
        </div>

        {/* Collapsible tool input */}
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-neutral-400 hover:text-neutral-200"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          aria-label="Toggle tool input details"
        >
          {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          Tool input
        </button>
        {expanded && (
          <pre className="max-h-48 overflow-auto rounded-md bg-neutral-900 p-3 text-xs text-neutral-300">
            {JSON.stringify(item.toolInput, null, 2)}
          </pre>
        )}

        {/* Resolved info */}
        {item.resolvedAt && (
          <p className="text-xs text-neutral-500">
            {item.status === 'escalated' ? 'Escalated' : 'Resolved'} by{' '}
            {item.resolvedBy ?? 'unknown'} {relativeTime(item.resolvedAt)}
            {item.denyMessage && <span className="text-red-400"> — {item.denyMessage}</span>}
          </p>
        )}

        {/* Action buttons */}
        {isPending && (
          <div className="flex items-center gap-2 pt-1">
            <Button
              size="sm"
              className="gap-1.5 bg-green-900/30 text-green-400 hover:bg-green-900/50"
              disabled={isResolving}
              onClick={() => onApprove(item.id)}
            >
              <CheckCircle className="size-3.5" />
              Approve
            </Button>
            <ModerationRejectDialog
              toolName={item.toolName}
              isPending={isResolving}
              onConfirm={(msg) => onReject(item.id, msg)}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
